/**
 * The five states a location can be in on the zone details page, and the rule
 * that assigns exactly one of them.
 *
 * Why this exists as its own module: the states used to be implicit, spread
 * over three conditions in updateLocations, and two of them shared one colour.
 * "Outside the zone circle" and "geocoding found nothing" both rendered red,
 * so an operator could not tell an address that is placed wrong from one that
 * was never placed at all. Those need different actions, so they need
 * different states.
 *
 * The rule is total and exclusive BY CONSTRUCTION: deriveLocationStatus is a
 * chain of returns whose last branch has no condition, so every input yields
 * exactly one key and no input yields none. It reads four booleans, so the
 * full cube is sixteen combinations, and locationStatus.test.js walks all of
 * them to keep it that way.
 *
 * Colours are hex without '#', matching the palette in constants/index.js.
 * Any colour used here must also be listed in COLORS there, because the map
 * markers are generated per colour at module load — a colour missing from
 * that list yields a marker with no image, silently.
 *
 * `dashed` says whether the list icon is drawn with a dashed ring. It tracks
 * ONE thing: whether the location has a position at all. That gives the list
 * a shape distinction on top of the colour one, which matters because yellow
 * and grey are close in luminance and hard to separate in greyscale. They are
 * never in the same shape group, so the pair never has to be told apart by
 * colour alone.
 */

export const LOCATION_STATUSES = [
  {
    key: 'located',
    label: 'I zonen',
    color: 'FFC444',
    dashed: false,
    description: 'Geokodad och inne i zoncirkeln.',
  },
  {
    key: 'adjusted',
    label: 'Justerad',
    color: '00C853',
    dashed: false,
    description: 'Positionen är satt för hand och ligger inne i zoncirkeln.',
  },
  {
    key: 'outside',
    label: 'Utanför zonen',
    color: '0D47A1',
    dashed: false,
    description: 'Har en position, men den ligger utanför zoncirkeln.',
  },
  {
    key: 'missing',
    label: 'Saknar position',
    color: 'E53935',
    dashed: true,
    description: 'Geokodningen gav ingen träff. Kräver åtgärd.',
  },
  {
    key: 'geocoding',
    label: 'Geokodas nu',
    color: 'd9d9d9',
    dashed: true,
    description: 'Ligger i geokodningskön just nu.',
  },
];

export const STATUS_BY_KEY = LOCATION_STATUSES.reduce((acc, s) => {
  acc[s.key] = s;
  return acc;
}, {});

/** Key for the "no filter" chip. Not a status — the zone centre has none. */
export const ALL_KEY = 'all';

/**
 * Assigns exactly one status key.
 *
 * Precedence note, deliberate and unchanged from the previous colour logic:
 * a location that is outside the circle reports "outside" even when it was
 * adjusted by hand. Being outside the zone is the fact that needs acting on;
 * that it got there manually is secondary.
 */
export function deriveLocationStatus({
  hasCoordinates,
  insideZone,
  updatedInZone,
  geocoding,
}) {
  if (!hasCoordinates) {
    return geocoding ? 'geocoding' : 'missing';
  }
  if (!insideZone) {
    return 'outside';
  }
  return updatedInZone ? 'adjusted' : 'located';
}

/**
 * Counts per status, plus `all`.
 *
 * The zone centre travels in the same array as the addresses (it is index 0,
 * type "zone") but it is not an address and must not appear in any count.
 * Filtering on type is what keeps every number talking about the same set.
 *
 * The invariant that makes the numbers trustworthy: the five status counts
 * sum to `all`. If a location ever carries an unknown status it lands in no
 * bucket, the sum comes up short, and the test catches it.
 */
export function countByStatus(locations) {
  const addresses = (locations || []).filter((l) => l && l.type === 'location');
  const counts = { [ALL_KEY]: addresses.length };
  LOCATION_STATUSES.forEach((s) => {
    counts[s.key] = 0;
  });
  addresses.forEach((l) => {
    if (Object.prototype.hasOwnProperty.call(STATUS_BY_KEY, l.status)) {
      counts[l.status] += 1;
    }
  });
  return counts;
}

/**
 * Applies the chip filter. Several states can be selected at once, so the
 * selection is a list of keys and the result is their union.
 *
 * An empty selection means "no filter": everything is shown, including the
 * zone centre. Pick any state and the centre drops out, because it has no
 * status and is not one of the things being counted.
 */
export function filterLocations(locations, statusKeys) {
  const keys = (statusKeys || []).filter((k) => k && k !== ALL_KEY);
  if (keys.length === 0) {
    return locations;
  }
  return (locations || []).filter(
    (l) => l && l.type === 'location' && keys.includes(l.status),
  );
}

/**
 * What the Excel export should contain: exactly the addresses on screen.
 *
 * The zone centre is never exported. It travels in the same array as the
 * addresses so it can be clicked in the list, but it is a property of the
 * zone, not an address, and a row for it in an address export has never been
 * anything but an artefact of that shared array.
 */
export function exportableLocations(locations, statusKeys) {
  // filterLocations returnerar sin parameter orörd när inget filter är valt,
  // så den kan ge tillbaka null. Resten av modulen tål null och den här ska
  // göra det också — anropet sker dessutom i renderkroppen, där ett kast
  // släcker hela zonvyn i stället för att fångas.
  return (filterLocations(locations, statusKeys) || []).filter(
    (l) => l && l.type === 'location',
  );
}
